import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { fetchUserOrders } from '../../redux/slices/orderSlice';

const MyOrdersPage = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { orders, loading, error } = useSelector((state) => state.orders);

  useEffect(() => {
    dispatch(fetchUserOrders());
  }, [dispatch]);

  const handleRowClick = (orderId) => {
    navigate(`/order/${orderId}`);
  };

  const formatDate = (dateStr) => {
    const d = new Date(dateStr);
    return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  const formatTime = (dateStr) => {
    return new Date(dateStr).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' });
  };

  /* ── LOADING ── */
  if (loading) return (
    <>
      <style>{ordersStyles}</style>
      <div className="mo-root">
        <div className="mo-state">
          <span className="mo-state-label">Loading Orders</span>
        </div>
      </div>
    </>
  );

  /* ── ERROR ── */
  if (error) return (
    <>
      <style>{ordersStyles}</style>
      <div className="mo-root">
        <div className="mo-state">
          <span className="mo-state-label">Something went wrong</span>
          <span className="mo-state-sub">{error}</span>
        </div>
      </div>
    </>
  );

  return (
    <>
      <style>{ordersStyles}</style>

      <div className="mo-root">

        {/* ── HEADER ── */}
        <div className="mo-header">
          <div>
            <span className="mo-eyebrow">Account</span>
            <h1 className="mo-title">My <span className="mo-title-bold">Orders</span></h1>
          </div>
          {orders.length > 0 && (
            <span className="mo-count">{orders.length} {orders.length === 1 ? 'order' : 'orders'}</span>
          )}
        </div>

        <div className="mo-rule-wrap"><hr className="mo-hr" /></div>

        {/* ── EMPTY STATE ── */}
        {orders.length === 0 && (
          <div className="mo-empty">
            <div className="mo-empty-icon">
              <svg width="36" height="36" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z" />
              </svg>
            </div>
            <span className="mo-empty-title">No Orders Yet</span>
            <span className="mo-empty-sub">Your orders will appear here once you make a purchase</span>
          </div>
        )}

        {/* ── DESKTOP TABLE ── */}
        {orders.length > 0 && (
          <div className="mo-table-wrap hidden md:block">
            <table className="mo-table">
              <thead>
                <tr className="mo-thead-row">
                  <th className="mo-th">Item</th>
                  <th className="mo-th">Order ID</th>
                  <th className="mo-th">Date</th>
                  <th className="mo-th">Shipping To</th>
                  <th className="mo-th mo-th--center">Items</th>
                  <th className="mo-th mo-th--right">Total</th>
                  <th className="mo-th mo-th--center">Status</th>
                </tr>
              </thead>
              <tbody>
                {orders.map((order) => (
                  <tr
                    key={order._id}
                    className="mo-row"
                    onClick={() => handleRowClick(order._id)}
                  >
                    <td className="mo-td">
                      <img
                        src={order.orderItems[0].image}
                        alt={order.orderItems[0].name}
                        className="mo-thumb"
                      />
                    </td>
                    <td className="mo-td">
                      <span className="mo-order-id">#{order._id.slice(-8).toUpperCase()}</span>
                    </td>
                    <td className="mo-td">
                      <span className="mo-date">{formatDate(order.createdAt)}</span>
                      <span className="mo-time">{formatTime(order.createdAt)}</span>
                    </td>
                    <td className="mo-td">
                      <span className="mo-address">
                        {order.shippingAddress
                          ? `${order.shippingAddress.address}, ${order.shippingAddress.state}, ${order.shippingAddress.country}`
                          : 'N/A'}
                      </span>
                    </td>
                    <td className="mo-td mo-td--center">
                      <span className="mo-items-count">{order.orderItems.length}</span>
                    </td>
                    <td className="mo-td mo-td--right">
                      <span className="mo-price">₹{Number(order.totalPrice).toLocaleString()}</span>
                    </td>
                    <td className="mo-td mo-td--center">
                      <span className={`mo-badge ${order.isPaid ? 'mo-badge--paid' : 'mo-badge--pending'}`}>
                        {order.isPaid ? 'Paid' : 'Pending'}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* ── MOBILE CARDS ── */}
        {orders.length > 0 && (
          <div className="mo-cards md:hidden">
            {orders.map((order) => (
              <div
                key={order._id}
                className="mo-card"
                onClick={() => handleRowClick(order._id)}
              >
                {/* Card top */}
                <div className="mo-card-top">
                  <img
                    src={order.orderItems[0].image}
                    alt={order.orderItems[0].name}
                    className="mo-card-img"
                  />
                  <div className="mo-card-info">
                    <span className="mo-card-name">{order.orderItems[0].name}</span>
                    {order.orderItems.length > 1 && (
                      <span className="mo-card-more">+{order.orderItems.length - 1} more</span>
                    )}
                    <span className={`mo-badge mo-badge--sm ${order.isPaid ? 'mo-badge--paid' : 'mo-badge--pending'}`}>
                      {order.isPaid ? 'Paid' : 'Pending'}
                    </span>
                  </div>
                  <span className="mo-card-price">₹{Number(order.totalPrice).toLocaleString()}</span>
                </div>

                <div className="mo-card-divider" />

                {/* Card details */}
                <div className="mo-card-details">
                  <div className="mo-card-row">
                    <span className="mo-card-label">Order ID</span>
                    <span className="mo-card-value">#{order._id.slice(-8).toUpperCase()}</span>
                  </div>
                  <div className="mo-card-row">
                    <span className="mo-card-label">Date</span>
                    <span className="mo-card-value">{formatDate(order.createdAt)}</span>
                  </div>
                  <div className="mo-card-row">
                    <span className="mo-card-label">Items</span>
                    <span className="mo-card-value">{order.orderItems.length}</span>
                  </div>
                  {order.shippingAddress && (
                    <div className="mo-card-row mo-card-row--full">
                      <span className="mo-card-label">Ship To</span>
                      <span className="mo-card-value mo-card-value--addr">
                        {order.shippingAddress.address}, {order.shippingAddress.city}, {order.shippingAddress.state}
                      </span>
                    </div>
                  )}
                </div>

                {/* Card footer */}
                <div className="mo-card-footer">
                  <span className="mo-card-cta">View Details</span>
                  <svg width="12" height="12" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </div>
              </div>
            ))}
          </div>
        )}

      </div>
    </>
  );
};

const ordersStyles = `
  @import url('https://fonts.googleapis.com/css2?family=Exo+2:wght@200;300;400;500;600&display=swap');

  .mo-root {
    background: #f8f8f8;
    min-height: 100vh;
    font-family: 'Exo 2', sans-serif;
    padding: 64px 80px 96px;
    max-width: 1400px;
    margin: 0 auto;
    box-sizing: border-box;
  }

  /* ── STATE ── */
  .mo-state {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 8px;
    padding: 80px 0;
  }
  .mo-state-label {
    font-size: 11px; font-weight: 500;
    letter-spacing: 0.35em; text-transform: uppercase;
    color: black;
  }
  .mo-state-sub {
    font-size: 10px; font-weight: 300;
    letter-spacing: 0.15em; color: black;
  }

  /* ── HEADER ── */
  .mo-header {
    display: flex;
    align-items: flex-end;
    justify-content: space-between;
    margin-bottom: 32px;
    gap: 16px;
  }

  .mo-eyebrow {
    display: flex;
    align-items: center;
    gap: 12px;
    font-size: 9px; font-weight: 400;
    letter-spacing: 0.5em; text-transform: uppercase;
    color: black;
    margin-bottom: 12px;
  }
  .mo-eyebrow::before {
    content: '';
    width: 24px; height: 1px;
    background: rgba(0,0,0,0.2);
    display: block; flex-shrink: 0;
  }

  .mo-title {
    font-size: clamp(32px, 4.5vw, 56px);
    font-weight: 200; color: #000;
    letter-spacing: 0.08em; text-transform: uppercase;
    line-height: 1; margin: 0;
  }
  .mo-title-bold { font-weight: 600; }

  .mo-count {
    font-size: 9px; font-weight: 300;
    letter-spacing: 0.35em; text-transform: uppercase;
    color: black;
    padding-bottom: 6px;
  }

  /* ── RULE ── */
  .mo-rule-wrap { margin-bottom: 40px; }
  .mo-hr { border: none; border-top: 1px solid rgba(0,0,0,0.08); margin: 0; }

  /* ── EMPTY ── */
  .mo-empty {
    display: flex; flex-direction: column;
    align-items: center; gap: 12px;
    padding: 80px 0;
  }
  .mo-empty-icon { color: black; margin-bottom: 8px; }
  .mo-empty-title {
    font-size: 11px; font-weight: 500;
    letter-spacing: 0.4em; text-transform: uppercase;
    color: black;
  }
  .mo-empty-sub {
    font-size: 10px; font-weight: 300;
    letter-spacing: 0.15em; color: black;
    text-align: center;
  }

  /* ── TABLE ── */
  .mo-table-wrap { overflow-x: auto; }

  .mo-table {
    width: 100%;
    border-collapse: collapse;
    font-family: 'Exo 2', sans-serif;
  }

  .mo-thead-row {
    border-bottom: 1px solid black;
  }

  .mo-th {
    font-size: 8px; font-weight: 500;
    letter-spacing: 0.4em; text-transform: uppercase;
    color: black;
    padding: 0 16px 14px 0;
    text-align: left;
    white-space: nowrap;
  }
  .mo-th--center { text-align: center; }
  .mo-th--right  { text-align: right; }

  .mo-row {
    border-bottom: 1px solid black;
    cursor: pointer;
    transition: background 0.15s ease;
  }
  .mo-row:hover { background: black; }
  .mo-row:last-child { border-bottom: none; }

  .mo-td {
    padding: 18px 16px 18px 0;
    vertical-align: middle;
  }
  .mo-td--center { text-align: center; }
  .mo-td--right  { text-align: right; }

  .mo-thumb {
    width: 56px; height: 68px;
    object-fit: cover; display: block;
  }

  .mo-order-id {
    font-size: 10px; font-weight: 500;
    letter-spacing: 0.15em; color: #000;
    font-variant-numeric: tabular-nums;
  }

  .mo-date {
    display: block;
    font-size: 11px; font-weight: 300;
    letter-spacing: 0.08em; color: #000;
  }
  .mo-time {
    display: block;
    font-size: 9px; font-weight: 300;
    letter-spacing: 0.1em; color: black;
    margin-top: 2px;
  }

  .mo-address {
    font-size: 11px; font-weight: 300;
    letter-spacing: 0.04em; color: black;
    max-width: 240px; display: block;
    overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
  }

  .mo-items-count {
    font-size: 12px; font-weight: 400;
    letter-spacing: 0.1em; color: #000;
  }

  .mo-price {
    font-size: 13px; font-weight: 500;
    letter-spacing: 0.06em; color: #000;
  }

  /* ── BADGE ── */
  .mo-badge {
    display: inline-block;
    font-family: 'Exo 2', sans-serif;
    font-size: 7px; font-weight: 600;
    letter-spacing: 0.3em; text-transform: uppercase;
    padding: 4px 10px;
  }
  .mo-badge--paid    { background: #000; color: #fff; }
  .mo-badge--pending { background: transparent; color: rgba(255, 0, 0, 1); border: 1px solid rgba(255, 0, 0, 1); }
  .mo-badge--sm { font-size: 6.5px; padding: 3px 8px; }

  /* ── MOBILE CARDS ── */
  .mo-cards {
    display: flex;
    flex-direction: column;
    gap: 2px;
  }

  .mo-card {
    background: #f8f8f8;
    border: 1px solid black;
    cursor: pointer;
    transition: border-color 0.2s ease;
  }
  .mo-card:hover { border-color: black; }

  .mo-card-top {
    display: flex;
    align-items: flex-start;
    gap: 14px;
    padding: 16px;
  }

  .mo-card-img {
    width: 64px; height: 76px;
    object-fit: cover; flex-shrink: 0;
  }

  .mo-card-info {
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: 6px;
    min-width: 0;
  }

  .mo-card-name {
    font-size: 10px; font-weight: 500;
    letter-spacing: 0.18em; text-transform: uppercase;
    color: #000; line-height: 1.4;
    overflow: hidden; text-overflow: ellipsis;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
  }

  .mo-card-more {
    font-size: 8px; font-weight: 300;
    letter-spacing: 0.25em; text-transform: uppercase;
    color: black;
  }

  .mo-card-price {
    font-size: 14px; font-weight: 500;
    letter-spacing: 0.05em; color: #000;
    flex-shrink: 0;
  }

  .mo-card-divider {
    height: 1px;
    background: black;
    margin: 0 16px;
  }

  .mo-card-details {
    padding: 12px 16px;
    display: flex;
    flex-direction: column;
    gap: 8px;
  }

  .mo-card-row {
    display: flex;
    justify-content: space-between;
    align-items: baseline;
    gap: 12px;
  }
  .mo-card-row--full { align-items: flex-start; }

  .mo-card-label {
    font-size: 8px; font-weight: 500;
    letter-spacing: 0.35em; text-transform: uppercase;
    color: black; flex-shrink: 0;
  }

  .mo-card-value {
    font-size: 11px; font-weight: 300;
    letter-spacing: 0.06em; color: #000;
    text-align: right;
  }
  .mo-card-value--addr { text-align: right; line-height: 1.5; }

  .mo-card-footer {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 10px 16px;
    border-top: 1px solid black;
  }

  .mo-card-cta {
    font-size: 8px; font-weight: 500;
    letter-spacing: 0.35em; text-transform: uppercase;
    color: black;
  }

  /* ── RESPONSIVE ── */
  @media (max-width: 1024px) {
    .mo-root { padding: 48px 40px 80px; }
  }

  @media (max-width: 768px) {
    .mo-root { padding: 40px 20px 64px; }
    .mo-header {
      flex-direction: column;
      align-items: flex-start;
      gap: 8px;
    }
    .mo-count { padding-bottom: 0; }
    .mo-rule-wrap { margin-bottom: 28px; }
  }

  @media (max-width: 360px) {
    .mo-root { padding: 32px 14px 48px; }
  }
`;

export default MyOrdersPage;
